// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { GroupIcon } from './alarms/GroupIcon';

/** One domain and how many items carry it. */
export interface AssetTypeChip {
  type: string;
  count: number;
}

/**
 * Filter chips by domain ("Electrical", "Mechanical", "Compute" …), with an
 * "All" chip first. Shared by the alarm list and the Asset Directory so both
 * classify an asset the same way: an alarm is about an asset, and its chip is
 * that asset's domain (utils/assetDomain, hooks/useAssetDomainOf).
 *
 * Controlled: `value` is the active type, or 'all'.
 */
export function AssetTypeChips({
  chips,
  total,
  value,
  onChange,
  label,
  labelOf,
  children,
}: {
  chips: AssetTypeChip[];
  /** Count shown on the "All" chip. */
  total: number;
  value: string;
  onChange: (_type: string) => void;
  /** Accessible name for the group, e.g. "Filter by domain". */
  label: string;
  /** Chip text for a type; the type itself is the filter key. Default: the type. */
  labelOf?: (_type: string) => string;
  /** Trailing status (e.g. "Refreshing…"), kept in the same row. */
  children?: React.ReactNode;
}) {
  return (
    <div className="hs-group-pills" role="group" aria-label={label}>
      <button
        type="button"
        className={`hs-group-pill${value === 'all' ? ' active' : ''}`}
        aria-pressed={value === 'all'}
        onClick={() => onChange('all')}
      >
        All <span className="hs-pill-count">{total}</span>
      </button>
      {chips.map(chip => (
        <button
          key={chip.type}
          type="button"
          className={`hs-group-pill${value === chip.type ? ' active' : ''}`}
          aria-pressed={value === chip.type}
          onClick={() => onChange(chip.type)}
        >
          <GroupIcon group={chip.type} size={13} />
          {labelOf ? labelOf(chip.type) : chip.type}{' '}
          <span className="hs-pill-count">{chip.count}</span>
        </button>
      ))}
      {children}
    </div>
  );
}

export default AssetTypeChips;
